import { useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { CheckCircle } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import {
  INSURANCE_CARD_CONTENT_TYPES,
  type InsuranceCardContentType,
  getInsuranceCardContentType,
  useInsuranceCardUploadMutation,
} from '@/api/hooks/mutations/use-insurance-card-upload-mutation';
import { GeneralErrorIndicator } from '@/components/GeneralErrorIndicator.tsx';
import { NavigationBar } from '@/components/NavigationBar';
import StepperCard from '@/components/common/StepperCard.tsx';
import { Button } from '@/components/ui/button.tsx';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { LOGGER_ACTIONS, logger } from '@/lib/logger';
import {
  getPostFamilyResolutionDestination,
  getResolveBackOverride,
  resolveFamily,
  setConfirmedFamilyInsurances,
} from '@/types/insurance-builder-eligibility-revamp.ts';
import type { InsuranceBuilder } from '@/types/insurance-builder.type.ts';
import {
  type CarrierType,
  type UnverifiedInsurance,
  carrierTypeToFamily,
} from '@/types/insurance.type.ts';
import {
  type CardSide,
  InsuranceCardUploadDropzone,
} from '@/views/insurance/components/InsuranceCardUploadDropzone.tsx';

const formSchema = z.object({
  frontFile: z.custom<File | null>((value) => isFile(value), {
    message: 'Please upload the front of your insurance card.',
  }),
  backFile: z.custom<File | null>((value) => isFile(value), {
    message: 'Please upload the back of your insurance card.',
  }),
});

type FormData = z.infer<typeof formSchema>;

const MAX_CARD_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const INSURANCE_CARD_CONTENT_TYPE_LABELS: Record<InsuranceCardContentType, string> = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/heic': 'HEIC',
  'application/pdf': 'PDF',
};
const ACCEPTED_INSURANCE_CARD_FORMATS = INSURANCE_CARD_CONTENT_TYPES.map(
  (contentType) => INSURANCE_CARD_CONTENT_TYPE_LABELS[contentType]
).join(', ');
const ACCEPTED_INSURANCE_CARD_CONTENT_TYPES = INSURANCE_CARD_CONTENT_TYPES.join(',');

function isFile(value: unknown): value is File {
  return typeof File !== 'undefined' && value instanceof File;
}

function getCardUploadAnalyticsProperties({
  backFile,
  carrierType,
  frontFile,
}: {
  backFile: File;
  carrierType: string;
  frontFile: File;
}) {
  return {
    carrier_type: carrierType,
    has_back_file: true,
    front_content_type: frontFile.type,
    front_size_bucket: getFileSizeBucket(frontFile),
    back_content_type: backFile.type,
    back_size_bucket: getFileSizeBucket(backFile),
  };
}

function getFileSizeBucket(file: File): string {
  const sizeInMb = file.size / (1024 * 1024);
  if (sizeInMb <= 1) return '0-1mb';
  if (sizeInMb <= 5) return '1-5mb';
  return '5-10mb';
}

function findUploadableInsurance(
  builder: InsuranceBuilder,
  carrierType: CarrierType
): UnverifiedInsurance | undefined {
  const matchesCarrierType = (insurance: UnverifiedInsurance) =>
    insurance.carrier.carrierType === carrierType;

  return (
    builder.unverifiedInsurances.find(matchesCarrierType) ??
    builder.confirmedInsurances.find(matchesCarrierType)
  );
}

function getCarrierTypes(insurances: UnverifiedInsurance[]): CarrierType[] {
  return insurances.map((insurance) => insurance.carrier.carrierType);
}

function getInsuranceDisplayName(insurance: UnverifiedInsurance): string {
  return insurance.carrier.displayName || insurance.carrier.name;
}

export function InsuranceUpload() {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();
  const { override, planType, preserveLaterFamilies, previousPlanFamily } = useSearch({
    from: '/appLayout/wizardLayout/insurance/upload',
  });
  const { insuranceBuilder } = useSessionStore();

  const parsedCarrierType = planType;
  const currentFamily = parsedCarrierType ? carrierTypeToFamily(parsedCarrierType) : null;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      frontFile: null,
      backFile: null,
    },
  });
  const frontFile = useWatch({ control: form.control, name: 'frontFile' });
  const backFile = useWatch({ control: form.control, name: 'backFile' });
  const [draggingSide, setDraggingSide] = useState<CardSide | null>(null);
  const [isReplacingUploadedCard, setIsReplacingUploadedCard] = useState(false);
  const insuranceCardUploadMutation = useInsuranceCardUploadMutation();
  const isSubmitting = form.formState.isSubmitting;
  const existingConfirmedInsurance = parsedCarrierType
    ? insuranceBuilder.confirmedInsurances.find(
        (insurance) => insurance.carrier.carrierType === parsedCarrierType
      )
    : undefined;
  const shouldShowUploadedCardState =
    Boolean(existingConfirmedInsurance) && !isReplacingUploadedCard;

  const setFileForSide = useCallback(
    (side: CardSide, selectedFile: File) => {
      form.setValue(side === 'front' ? 'frontFile' : 'backFile', selectedFile, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form]
  );

  const clearFileForSide = useCallback(
    (side: CardSide) => {
      form.setValue(side === 'front' ? 'frontFile' : 'backFile', null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form]
  );

  const handleDragOver = useCallback((side: CardSide, e: React.DragEvent) => {
    e.preventDefault();
    setDraggingSide(side);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDraggingSide(null);
  }, []);

  const handleDrop = useCallback(
    (side: CardSide, e: React.DragEvent) => {
      e.preventDefault();
      setDraggingSide(null);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        setFileForSide(side, droppedFile);
      }
    },
    [setFileForSide]
  );

  const handleFileSelect = useCallback(
    (side: CardSide, e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        setFileForSide(side, selectedFile);
      }
      e.target.value = '';
    },
    [setFileForSide]
  );

  const validateFileForSide = useCallback(
    (side: CardSide, file: File): boolean => {
      const field = side === 'front' ? 'frontFile' : 'backFile';
      if (file.size > MAX_CARD_FILE_SIZE_BYTES) {
        form.setError(field, { message: 'Please upload a file smaller than 10MB.' });
        return false;
      }

      if (!getInsuranceCardContentType(file)) {
        form.setError(field, {
          message: `Please upload a ${ACCEPTED_INSURANCE_CARD_FORMATS} file.`,
        });
        return false;
      }

      return true;
    },
    [form]
  );

  const navigateAfterInsuranceConfirmed = useCallback(
    (updatedBuilder: InsuranceBuilder) => {
      if (!parsedCarrierType || !currentFamily) return;

      const destination = getPostFamilyResolutionDestination(updatedBuilder, currentFamily);
      if (destination.type === 'resolve') {
        navigate({
          to: '/insurance/resolve',
          search: {
            planFamily: destination.planFamily,
            previousUploadPlanType: parsedCarrierType,
            previousPlanFamily: destination.previousPlanFamily,
            ...(preserveLaterFamilies && { preserveLaterFamilies }),
          },
          replace: true,
        });
        return;
      }

      if (destination.type === 'ineligible') {
        navigate({
          to: '/insurance/ineligible',
          search: { reason: destination.reason },
          replace: true,
        });
        return;
      }

      navigate({ to: '/insurance/benefits', replace: true });
    },
    [currentFamily, navigate, parsedCarrierType, preserveLaterFamilies]
  );

  const confirmInsuranceAndContinue = useCallback(
    (insuranceToConfirm: UnverifiedInsurance) => {
      if (!currentFamily) return;

      let updatedBuilder = setConfirmedFamilyInsurances(insuranceBuilder, insuranceToConfirm);
      updatedBuilder = resolveFamily(updatedBuilder, currentFamily, true);

      useSessionStore.getState().updateState({
        insuranceBuilder: { ...updatedBuilder, ranVerification: true },
      });
      saveSession();

      navigateAfterInsuranceConfirmed(updatedBuilder);
    },
    [currentFamily, insuranceBuilder, navigateAfterInsuranceConfirmed, saveSession]
  );

  const handleContinueWithUploadedCard = useCallback(() => {
    if (!existingConfirmedInsurance) return;

    form.clearErrors('root');
    confirmInsuranceAndContinue(existingConfirmedInsurance);
  }, [confirmInsuranceAndContinue, existingConfirmedInsurance, form]);

  const handleSubmit = useCallback(
    async (submittedFormData: FormData) => {
      if (!parsedCarrierType || !currentFamily) return;

      const insuranceToConfirm = findUploadableInsurance(insuranceBuilder, parsedCarrierType);

      if (!insuranceToConfirm) {
        logger.error(new Error('Insurance upload missing uploadable insurance record'), {
          carrierType: parsedCarrierType,
          unverifiedCarrierTypes: getCarrierTypes(insuranceBuilder.unverifiedInsurances),
          confirmedCarrierTypes: getCarrierTypes(insuranceBuilder.confirmedInsurances),
        });

        form.setError('root', {
          message:
            'We need your insurance details before you upload your card. Please go back and enter your plan information again.',
        });
        return;
      }

      if (!submittedFormData.frontFile) {
        form.setError('frontFile', { message: 'Please upload the front of your insurance card.' });
        return;
      }
      if (!submittedFormData.backFile) {
        form.setError('backFile', { message: 'Please upload the back of your insurance card.' });
        return;
      }

      if (!validateFileForSide('front', submittedFormData.frontFile)) return;
      if (!validateFileForSide('back', submittedFormData.backFile)) return;

      const uploadAnalyticsProperties = getCardUploadAnalyticsProperties({
        backFile: submittedFormData.backFile,
        carrierType: parsedCarrierType,
        frontFile: submittedFormData.frontFile,
      });

      logger.action(LOGGER_ACTIONS.INTAKE.INSURANCE_CARD_UPLOAD_SUBMITTED, {
        ...uploadAnalyticsProperties,
      });

      form.clearErrors('root');

      try {
        await insuranceCardUploadMutation.mutateAsync({
          verificationKey: insuranceToConfirm.verificationKey,
          frontFile: submittedFormData.frontFile,
          backFile: submittedFormData.backFile,
        });

        logger.action(LOGGER_ACTIONS.INTAKE.INSURANCE_CARD_UPLOAD_COMPLETED, {
          ...uploadAnalyticsProperties,
        });

        confirmInsuranceAndContinue(insuranceToConfirm);
      } catch (error) {
        logger.error('Failed to upload insurance card', error, {
          carrierType: parsedCarrierType,
          hasBackFile: Boolean(submittedFormData.backFile),
          frontContentType: submittedFormData.frontFile.type,
          frontSize: submittedFormData.frontFile.size,
          backContentType: submittedFormData.backFile?.type,
          backSize: submittedFormData.backFile?.size,
        });

        form.setError('root', {
          message: "We couldn't upload your insurance card. Please try again.",
        });
      }
    },
    [
      parsedCarrierType,
      currentFamily,
      insuranceBuilder,
      form,
      validateFileForSide,
      insuranceCardUploadMutation,
      confirmInsuranceAndContinue,
    ]
  );

  const handleSkipUpload = () => {
    logger.action(LOGGER_ACTIONS.INTAKE.INSURANCE_CARD_UPLOAD_SKIPPED, {
      carrier_type: parsedCarrierType,
      plan_family: currentFamily,
    });

    navigate({
      to: '/insurance/send-link',
      search: {
        planFamily: currentFamily ?? undefined,
        planType: parsedCarrierType,
        override,
        previousPlanFamily,
        ...(preserveLaterFamilies && { preserveLaterFamilies }),
      },
    });
  };

  const toResolve = () => {
    if (currentFamily) {
      const previousResolveFamily =
        previousPlanFamily === currentFamily ? undefined : previousPlanFamily;

      navigate({
        to: '/insurance/resolve',
        search: {
          planFamily: currentFamily,
          override: getResolveBackOverride({
            builder: insuranceBuilder,
            family: currentFamily,
            override,
          }),
          previousPlanFamily: previousResolveFamily,
          ...(preserveLaterFamilies && { preserveLaterFamilies }),
        },
        replace: true,
      });
    } else {
      navigate({ to: '/insurance/resolve', search: { planFamily: 'medicare' }, replace: true });
    }
  };

  return (
    <section>
      <NavigationBar onBack={toResolve} backLabel="Back" stepIndicator="2 of 3" />
      <StepperCard currentStep={2} totalSteps={3}>
        <CardHeader>
          <CardTitle>
            {shouldShowUploadedCardState
              ? 'Your insurance card was uploaded'
              : 'Upload a photo of your insurance card'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {shouldShowUploadedCardState && existingConfirmedInsurance ? (
            <div className="space-y-6">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <div className="flex gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                  <div className="space-y-1">
                    <p className="text-charcoal-900 text-sm font-medium">
                      Card photos saved for {getInsuranceDisplayName(existingConfirmedInsurance)}
                    </p>
                    <p className="text-charcoal-700 text-sm">
                      You can continue with the card photos you already uploaded, or replace them
                      with new photos.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <Button
                  className="w-full sm:w-auto sm:min-w-[220px]"
                  type="button"
                  onClick={handleContinueWithUploadedCard}
                >
                  Continue with uploaded card
                </Button>

                <Button
                  className="w-full sm:w-auto sm:px-0"
                  variant="link"
                  size="sm"
                  type="button"
                  onClick={() => setIsReplacingUploadedCard(true)}
                >
                  Replace uploaded photos
                </Button>
              </div>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
              <p className="text-charcoal-700 text-sm">
                Please take a clear photo of your insurance card so we can verify your coverage.
              </p>

              <div className="flex flex-col gap-4">
                <InsuranceCardUploadDropzone
                  accept={ACCEPTED_INSURANCE_CARD_CONTENT_TYPES}
                  file={frontFile}
                  isDragging={draggingSide === 'front'}
                  label="Front of card"
                  side="front"
                  onDragLeave={handleDragLeave}
                  onDragOver={(event) => handleDragOver('front', event)}
                  onDrop={(event) => handleDrop('front', event)}
                  onFileSelect={(event) => handleFileSelect('front', event)}
                  onRemove={() => clearFileForSide('front')}
                  error={form.formState.errors.frontFile?.message}
                  required
                />
                <InsuranceCardUploadDropzone
                  accept={ACCEPTED_INSURANCE_CARD_CONTENT_TYPES}
                  file={backFile}
                  isDragging={draggingSide === 'back'}
                  label="Back of card"
                  side="back"
                  onDragLeave={handleDragLeave}
                  onDragOver={(event) => handleDragOver('back', event)}
                  onDrop={(event) => handleDrop('back', event)}
                  onFileSelect={(event) => handleFileSelect('back', event)}
                  onRemove={() => clearFileForSide('back')}
                  error={form.formState.errors.backFile?.message}
                  required
                />
              </div>

              <p className="text-charcoal-500 text-xs">
                Accepted formats: {ACCEPTED_INSURANCE_CARD_FORMATS}. Maximum file size: 10MB.
              </p>

              {form.formState.errors.root?.message && (
                <GeneralErrorIndicator>{form.formState.errors.root.message}</GeneralErrorIndicator>
              )}

              <div className="space-y-4">
                <Button
                  className="w-full sm:w-auto sm:min-w-[186px]"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Uploading...' : 'Upload and Continue'}
                </Button>

                <Button
                  className="w-full sm:w-auto sm:px-0"
                  variant="link"
                  size="sm"
                  onClick={handleSkipUpload}
                  type="button"
                >
                  I don't have my insurance card
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </StepperCard>
    </section>
  );
}
